import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Organisation, Person, Shapeable, Slice } from '@shapeable/types';
import { breakpoints, theme, ColorTokens } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { ChevronRightThinIconGlyph, EmailFilledIconGlyph } from '@shapeable/icons';
import { DottedLine, IconTextValue, LinkButton, SliceMainSectionLayout, useActivePerson, useAuth, useLang, usePageEntity } from '@shapeable/ui';


const cls = classNames('slice-contact-info');

// -------- Types -------->

// -------- Props -------->

export type SliceContactEmailProps = Classable & HasChildren & { 
  entity?: Slice;
  colors?: ColorTokens.TextBackgroundBorderStrokeFill;
  showDownloadContactButton?: boolean;
  showPlatformConnectButtons?: boolean;
};

export const SliceContactEmailDefaultProps: Omit<SliceContactEmailProps, 'entity'> = {
  showDownloadContactButton: false,
  colors: {
    fill: 'line',
    border: 'line',
    text: 'line',
    hover: {
      fill: 'mid',
      border: 'mid',
      text: 'mid',
    },
  }
};

// -------- Child Component Props -------->

type ContainerProps = {

};

type BodyProps = {
  hasContactInfo: boolean;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    font-size: ${theme.FONT_SIZE(16)};
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;

    ${({ hasContactInfo }: BodyProps) => hasContactInfo && css`
      gap: ${theme.UNIT(4)};
    `}
  `,
});

const TextValueStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('strong')};
    font-weight: 300;
    font-size: 1em;
  `,
});

const HeadStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: ${theme.UNIT(4)};
  `,
});

const ButtonsStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.UNIT(4)};
  `,
});

const DownloadStyles = breakpoints({
  base: css`
    
  `,
});

const PlatformConnectStyles = breakpoints({
  base: css`
    
  `,
});



// -------- Components -------->

const My = {
  Container: styled(SliceMainSectionLayout)<ContainerProps>`${ContainerStyles}`,
    Body: styled.div<BodyProps>`${BodyStyles}`,
      Head: styled.div`${HeadStyles}`,
        TextValue: styled(IconTextValue)`${TextValueStyles}`,

      Buttons: styled.div`${ButtonsStyles}`,
        Download: styled(LinkButton)`${DownloadStyles}`,
        PlatformConnect: styled(LinkButton)`${PlatformConnectStyles}`,
};

export const SliceContactEmail: Shapeable.FC<SliceContactEmailProps> = (props) => {
  const componentProps = props;
  const { className, children, entity, colors, showDownloadContactButton } = componentProps;

  const activeEntity = usePageEntity() as Person;
  const { isAuthenticated } = useAuth() || {};
  const { person } = useActivePerson();
  const { email: sliceEmail  } = entity;

  const email = sliceEmail || activeEntity?.email;

  const hasContactInfo = email;
  const isOwnProfile = person?.slug === activeEntity?.slug;
  const hasEmail = !!email && isAuthenticated && (!activeEntity?.hideEmail || isOwnProfile);

  const t = useLang();

  // leave vCard WITHOUT indentation to avoid whitespace in the file
  const downloadVCard = () => {
    const vCard = `
BEGIN:VCARD
VERSION:3.0
FN:${activeEntity?.name || 'Unknown'}
EMAIL:${email || activeEntity?.email || ''}
END:VCARD
    `.trim();
  
    const blob = new Blob([vCard], { type: 'text/vcard' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeEntity?.name || 'contact'}.vcf`;
    link.click();
  };

  return (
    <My.Container className={cls.name(className)} entity={entity}>
      <My.Body hasContactInfo={!!hasContactInfo} >
        <My.Head>
          {
            hasEmail &&
            <My.TextValue iconComponent={EmailFilledIconGlyph} colors={{ fill: 'text' }} iconSize={30}>{email}</My.TextValue>
          }
        </My.Head>
        {
          showDownloadContactButton && (
            <>
              {
                hasContactInfo && <DottedLine height={2} gap={7} colors={colors} />
              }
              <My.Buttons>
                <My.Download 
                  onClick={(e) => { e.preventDefault(); downloadVCard(); }} 
                  colors={colors} 
                  href={'#'} 
                  shape='rounded' 
                  borderStyle='solid'
                  icon={ChevronRightThinIconGlyph}>
                    {t('Download Contact')}
                  </My.Download>
              </My.Buttons>
            </>
          )
        }
      </My.Body>
    </My.Container>
  )
};

SliceContactEmail.defaultProps = SliceContactEmailDefaultProps;
SliceContactEmail.cls = cls;